import { useQuery } from '@tanstack/react-query';
import type { components } from '@trackercop/contracts';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-keys';
import { PayloadCategoryChip } from '@/components/category-chip';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import type { RecipientTableRow } from '@/lib/site-data-flow-graph';

type DataFlowVendor = components['schemas']['DataFlowVendor'];
type DataFlowPage = components['schemas']['DataFlowPage'];
type ConsentMode = components['schemas']['ConsentMode'];
type PayloadCategory = components['schemas']['PayloadCategory'];

interface VendorCardProps {
  open: boolean;
  vendor: DataFlowVendor;
  recipientRow?: RecipientTableRow | null;
  /** Latest crawl id for the lazy parameter-sample fetch. */
  crawlId: string;
  /**
   * The page currently filtered into the graph (`?pageId=`), or null
   * when the view is site-level. Surfaces the "Show all pages" path
   * inside the card.
   */
  activePageId?: string | null;
  /** Pages observed in the latest crawl — drives the "View by page" select. */
  availablePages?: readonly DataFlowPage[];
  /** Notify the host route that the page filter changed (null clears it). */
  onSelectPage?: (pageId: string | null) => void;
  onClose: () => void;
}

const CONSENT_MODE_LABEL: Record<ConsentMode, string> = {
  'no-consent': 'No consent',
  'accept-all': 'Accept all',
  'reject-all': 'Reject all',
};

// Right-side sheet that surfaces the "what data is going where" answer
// for a single vendor. Top parameter names + redacted samples are
// fetched lazily on open (CLAUDE.md Phase 3a rule — never on row
// render). This first-pass card uses the existing payloads endpoint
// filtered by `destinationDomain` for the lazy fetch; a vendor-aware
// endpoint is plan Phase C.
export function VendorCard({
  open,
  vendor,
  recipientRow,
  crawlId,
  activePageId = null,
  availablePages,
  onSelectPage,
  onClose,
}: VendorCardProps) {
  const samplesQuery = useQuery({
    queryKey: [
      ...queryKeys.crawls.payloads(crawlId, {
        destinationDomain: vendor.primaryDomain,
        page: 1,
        pageSize: 10,
      }),
      'vendor-card-samples',
    ] as const,
    enabled: open,
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/crawls/{crawlId}/payloads', {
        params: {
          path: { crawlId },
          query: {
            destinationDomain: vendor.primaryDomain,
            page: 1,
            pageSize: 10,
          },
        },
      });
      if (error) throw error;
      return data;
    },
  });

  if (!open) return null;

  return (
    <aside
      role="dialog"
      aria-label={`${vendor.displayName} details`}
      className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col gap-4 overflow-y-auto border-l bg-background p-4 shadow-lg"
      data-testid="vendor-card"
    >
      <header className="flex items-start justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold" data-testid="vendor-card-name">
            {vendor.displayName}
          </h2>
          <p className="font-mono text-xs text-muted-foreground">
            {vendor.primaryDomain}
          </p>
          {vendor.parentVendorId && (
            <p className="text-xs text-muted-foreground">
              Parent vendor: <span className="font-mono">{vendor.parentVendorId}</span>
            </p>
          )}
          {vendor.cloakedVia && (
            <p className="text-xs text-amber-700" data-testid="vendor-card-cloaked-via">
              Reached via CNAME from{' '}
              <span className="font-mono">{vendor.cloakedVia}</span>
            </p>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={onClose} aria-label="Close">
          ×
        </Button>
      </header>

      {vendor.cnameChain && vendor.cnameChain.length > 1 && (
        <section
          className="flex flex-col gap-1"
          data-testid="vendor-card-cname-chain"
        >
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            DNS chain
          </h3>
          <ol className="flex flex-col gap-0.5 rounded-sm border bg-muted/30 px-2 py-1 font-mono text-[11px]">
            {vendor.cnameChain.map((link, idx) => (
              <li
                key={`${link}-${idx}`}
                className="flex items-center gap-2"
                data-testid="vendor-card-cname-link"
              >
                <span className="w-4 text-right text-muted-foreground">
                  {idx === 0 ? '→' : '↳'}
                </span>
                <span>{link}</span>
              </li>
            ))}
          </ol>
        </section>
      )}

      <section className="flex flex-wrap gap-2" data-testid="vendor-card-actions">
        {onSelectPage && (
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              View by page
            </label>
            <Select
              className="h-8 w-56"
              value={activePageId ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                onSelectPage(v === '' ? null : v);
              }}
              data-testid="vendor-card-page-select"
            >
              <option value="">All pages</option>
              {(availablePages ?? []).map((p) => (
                <option key={p.nodeId} value={p.pageId}>
                  page {p.pageId.slice(0, 8)}…
                </option>
              ))}
            </Select>
          </div>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            const params = new URLSearchParams();
            params.set('tab', 'payloads');
            params.set('vendorId', vendor.id);
            params.set('destinationDomain', vendor.primaryDomain);
            const url = `/crawls/${encodeURIComponent(crawlId)}?${params.toString()}`;
            window.open(url, '_blank', 'noopener,noreferrer');
          }}
          data-testid="vendor-card-view-payloads"
        >
          View payloads
        </Button>
      </section>

      {recipientRow && (
        <section className="flex flex-col gap-2">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Recipient summary
            </h3>
            <p className="text-sm">
              <span className="font-mono">{recipientRow.totalRequests}</span> payloads across{' '}
              <span className="font-mono">{recipientRow.pagesContacted}</span>{' '}
              {recipientRow.pagesContacted === 1 ? 'page' : 'pages'}.
            </p>
          </div>
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Categories observed
            </h3>
            <div
              className="flex flex-wrap gap-1"
              data-testid="vendor-card-categories"
            >
              {Array.from(recipientRow.categories)
                .sort()
                .map((c: PayloadCategory) => (
                  <PayloadCategoryChip key={c} category={c} />
                ))}
            </div>
          </div>
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Consent modes
            </h3>
            <p className="text-xs">
              {Array.from(recipientRow.consentModes)
                .map((m) => CONSENT_MODE_LABEL[m])
                .sort()
                .join(', ')}
            </p>
          </div>
        </section>
      )}

      <section className="flex flex-col gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Top parameter names (redacted)
        </h3>
        {samplesQuery.isLoading ? (
          <p className="text-xs text-muted-foreground">Loading samples…</p>
        ) : samplesQuery.isError ? (
          <p className="text-xs text-rose-700">Failed to load samples.</p>
        ) : samplesQuery.data && samplesQuery.data.items.length > 0 ? (
          <ul
            className="flex flex-col gap-1 text-xs"
            data-testid="vendor-card-samples"
          >
            {topParameterNames(samplesQuery.data.items).map((p) => (
              <li
                key={p.name}
                className="flex items-center justify-between rounded-sm border bg-muted/30 px-2 py-1"
              >
                <span className="font-mono">{p.name}</span>
                <span className="text-muted-foreground">×{p.count}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-muted-foreground">No samples observed.</p>
        )}
      </section>
    </aside>
  );
}

interface ParameterCount {
  name: string;
  count: number;
}

type PayloadField = components['schemas']['PayloadField'];
type PayloadRecord = components['schemas']['PayloadRecord'];

function isOverflowRef(
  body: PayloadRecord['bodyFields'],
): body is components['schemas']['PayloadOverflowRef'] {
  return !!body && !Array.isArray(body) && 'overflowKey' in body;
}

function topParameterNames(records: readonly PayloadRecord[]): ParameterCount[] {
  const counts = new Map<string, number>();
  for (const r of records) {
    const body: PayloadField[] = isOverflowRef(r.bodyFields)
      ? []
      : (r.bodyFields ?? []);
    const query: PayloadField[] = r.queryFields ?? [];
    for (const f of [...body, ...query]) {
      if (!f.name) continue;
      counts.set(f.name, (counts.get(f.name) ?? 0) + 1);
    }
  }
  return Array.from(counts.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
    .slice(0, 10);
}
